import { browser } from "$app/environment";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const COOKIE_NAME = "maxxus_theme";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

let preference = $state<ThemePreference>("system");
let resolved = $state<ResolvedTheme>("light");

let mediaQuery: MediaQueryList | null = null;
let mediaListener: ((event: MediaQueryListEvent) => void) | null = null;

function readSystemPreference(): ResolvedTheme {
  if (!browser || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolve(pref: ThemePreference): ResolvedTheme {
  return pref === "system" ? readSystemPreference() : pref;
}

function applyToDocument(theme: ResolvedTheme): void {
  if (!browser) return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

function writeCookie(value: ThemePreference): void {
  if (!browser) return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function readCookie(): ThemePreference | null {
  if (!browser) return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  const value = match?.[1];
  if (value === "light" || value === "dark" || value === "system") return value;
  return null;
}

function attachSystemListener(): void {
  if (!browser || mediaQuery !== null) return;
  mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  mediaListener = (event) => {
    if (preference === "system") {
      const next: ResolvedTheme = event.matches ? "dark" : "light";
      resolved = next;
      applyToDocument(next);
    }
  };
  mediaQuery.addEventListener("change", mediaListener);
}

export const theme = {
  get preference(): ThemePreference {
    return preference;
  },
  get resolved(): ResolvedTheme {
    return resolved;
  },
  init(): void {
    if (!browser) return;
    const stored = readCookie();
    preference = stored ?? "system";
    resolved = resolve(preference);
    applyToDocument(resolved);
    attachSystemListener();
  },
  set(next: ThemePreference): void {
    preference = next;
    resolved = resolve(next);
    writeCookie(next);
    applyToDocument(resolved);
  },
  cycle(): void {
    const order: ThemePreference[] = ["light", "dark", "system"];
    const idx = order.indexOf(preference);
    this.set(order[(idx + 1) % order.length] ?? "system");
  },
};
